import { Injectable, inject, signal } from '@angular/core';
import { Observable, defer, firstValueFrom, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import html2canvas from 'html2canvas-pro';
import { environment } from 'src/environments/environment';

export interface ScreenshotOptions {
    //  Element to capture. Defaults to document.body
    element?: HTMLElement;
    fileName?: string;
    mimeType?: 'image/png' | 'image/jpeg' | 'image/webp';
    quality?: number;
    scale?: number;
    backgroundColor?: string;
    extraParams?: Record<string, string | number | boolean>;
    baseUrl?: string;
    api?: string;
    /**
     * When set, the uploaded s3 url is remembered in localStorage against this
     * key. A retry with the same key skips the capture + upload completely and
     * returns the stored url, so a failed payment retried by the user does not
     * push the same screenshot to s3 twice.
     */
    cacheKey?: string;
    /**
     * By default every scroll container is unclipped so its whole content lands
     * in the shot. Set this to keep the current scroll positions instead and
     * capture only what the user can actually see on screen right now. With
     * document.body as the element, the shot is cropped to the browser window.
     */
    visibleOnly?: boolean;
}

export interface ScreenshotUploadResponse {
    success: boolean;
    url?: string;
    response?: any;
    error?: string;
    /** true when the url came from a previous upload instead of a fresh one */
    cached?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ScreenshotService {

    private readonly http = inject(HttpClient);

    readonly uploadApi = 'upload_screenshot';

    private readonly ignoreAttribute = 'data-html2canvas-ignore';

    /**
     * Every remote image inside the captured element is fetched before
     * html2canvas runs, so this timeout sits directly in front of the user with
     * the loader up. Keep it short - an inlined logo is cosmetic, and a slow
     * image host must not hold up a payment.
     */
    private readonly imageTimeout = 3000;

    /**
     * data urls already resolved this session, keyed by image src. Logos do not
     * change mid-session, so a retried payment skips the network entirely. An
     * empty string is cached too - a src that failed cors once will fail again,
     * and re-paying should not pay the timeout a second time.
     */
    private readonly inlinedImages = new Map<string, string>();

    /** localStorage bucket holding { cacheKey: s3Url } for already sent shots */
    private readonly cacheStorageKey = 'uploaded_screenshots';

    private readonly capturing = signal(false);
    readonly isCapturing = this.capturing.asReadonly();

    async capture(options: ScreenshotOptions = {}): Promise<Blob> {
        const element = options.element ?? document.body;

        if (!element) {
            throw new Error('Element not found for screenshot');
        }

        const mimeType = options.mimeType ?? 'image/jpeg';
        const quality = options.quality ?? 0.85;

        this.capturing.set(true);

        try {
            const inlined = await this.inlineImages(element);

            const isPage = element === document.body || element === document.documentElement;
            const viewport = options.visibleOnly && isPage
                ? { x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight }
                : {};

            const canvas = await html2canvas(element, {
                ...viewport,
                scale: options.scale ?? 1,
                backgroundColor: options.backgroundColor ?? '#ffffff',
                useCORS: true,
                allowTaint: false,
                logging: false,
                ignoreElements: (el: Element) => el.tagName === 'NOSCRIPT' || el.hasAttribute(this.ignoreAttribute),
                onclone: (_document: Document, clone: HTMLElement) => {
                    if (!options.visibleOnly) {
                        this.expandScrollContainers(clone);
                    }
                    this.applyInlinedImages(clone, inlined);
                }
            });

            return await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Unable to generate screenshot')),
                    mimeType,
                    quality
                );
            });
        } finally {
            this.capturing.set(false);
        }
    }

        private async inlineImages(element: HTMLElement): Promise<Map<string, string>> {
        // one fetch per url, but the result has to answer for both src and currentSrc
        const targets = new Map<string, string[]>();

        for (const img of this.imagesIn(element)) {
            const src = img.currentSrc || img.src;

            if (!src || !this.isCrossOrigin(src) || img.hasAttribute(this.ignoreAttribute)) {
                continue;
            }

            const keys = targets.get(src) ?? [];
            for (const key of [img.src, img.currentSrc]) {
                if (key && !keys.includes(key)) {
                    keys.push(key);
                }
            }
            targets.set(src, keys);
        }

        const inlined = new Map<string, string>();

        await Promise.all(Array.from(targets).map(async ([src, keys]) => {
            const dataUrl = this.inlinedImages.has(src)
                ? this.inlinedImages.get(src)
                : await this.toDataUrlFromUrl(src).then((result) => {
                    this.inlinedImages.set(src, result);
                    return result;
                });

            if (dataUrl) {
                keys.forEach((key) => inlined.set(key, dataUrl));
            }
        }));

        return inlined;
    }

    private applyInlinedImages(clone: HTMLElement, inlined: Map<string, string>): void {
        if (!inlined.size) {
            return;
        }

        for (const img of this.imagesIn(clone)) {
            const dataUrl = inlined.get(img.src) ?? inlined.get(img.currentSrc);

            if (dataUrl) {
                img.removeAttribute('srcset');
                img.setAttribute('src', dataUrl);
            }
        }
    }

    private imagesIn(root: HTMLElement): HTMLImageElement[] {
        const nested = Array.from(root.querySelectorAll('img'));
        return root.tagName === 'IMG' ? [root as HTMLImageElement, ...nested] : nested;
    }

    private isCrossOrigin(src: string): boolean {
        if (/^(data:|blob:)/i.test(src)) {
            return false;
        }
        try {
            return new URL(src, document.baseURI).origin !== window.location.origin;
        } catch {
            return false;
        }
    }

    private async toDataUrlFromUrl(src: string): Promise<string> {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.imageTimeout);

        try {
            // 'reload' so a copy cached without the cors headers is never reused
            const response = await fetch(src, {
                mode: 'cors',
                credentials: 'omit',
                cache: 'reload',
                signal: controller.signal
            });

            if (!response.ok) {
                return '';
            }

            return await this.blobToDataUrl(await response.blob());
        } catch {
            // blocked by cors, 403, offline - keep the original src
            return '';
        } finally {
            clearTimeout(timer);
        }
    }

    private blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Runs inside html2canvas's onclone, i.e. squarely inside the window where
     * the main thread is blocked and the loader is frozen on screen, so it is
     * written to keep that window short:
     *  - the cheap overflow check gates the expensive getComputedStyle, which
     *    otherwise resolves a full computed style for every node on the page;
     *  - reads are finished before any write, so unclip()'s style changes cannot
     *    invalidate layout for the node measured on the next iteration. Mixing
     *    them forces a reflow per node.
     */
    private expandScrollContainers(clone: HTMLElement): void {
        for (let node: HTMLElement | null = clone; node; node = node.parentElement) {
            this.unclip(node);
        }

        const view = clone.ownerDocument.defaultView;
        const scrollable: HTMLElement[] = [];

        for (const node of Array.from(clone.querySelectorAll<HTMLElement>('*'))) {
            if (node.scrollHeight <= node.clientHeight && node.scrollWidth <= node.clientWidth) {
                continue;
            }

            const style = view?.getComputedStyle(node);

            if (style && /(auto|scroll)/.test(style.overflowY + style.overflowX)) {
                scrollable.push(node);
            }
        }

        scrollable.forEach((node) => this.unclip(node));
    }

    private unclip(node: HTMLElement): void {
        node.style.setProperty('overflow', 'visible', 'important');
        node.style.setProperty('height', 'auto', 'important');
        node.style.setProperty('max-height', 'none', 'important');
    }

    async captureAsDataUrl(options: ScreenshotOptions = {}): Promise<string> {
        const blob = await this.capture(options);

        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Unable to read screenshot'));
            reader.readAsDataURL(blob);
        });
    }

    //creating the own http client sending observable as do not want interceptor to throw error on failuer as this is a silent service
    upload(blob: Blob, options: ScreenshotOptions = {}): Observable<any> {
        const mimeType = options.mimeType ?? blob.type ?? 'image/jpeg';
        const fileName = options.fileName ?? this.buildFileName(mimeType);
        const file = new File([blob], fileName, { type: mimeType });

        const form = new FormData();
        form.append('file', file);
        form.append('access_token', localStorage.getItem('token') ?? '');

        for (const [key, value] of Object.entries(options.extraParams ?? {})) {
            if (value !== undefined && value !== null) {
                form.append(key, String(value));
            }
        }

        // The caller owns the global loader for the whole payment sequence
        // (capture -> this upload -> pay_pf). InterceptorService skips its own
        // show/hide when the body carries hideLoader, so without this the overlay
        // is torn down the moment this upload lands and has to be shown again for
        // the payment - which reads to the user as the loader being stuck.
        // Set as a plain property, not a form field: the multipart payload is
        // built only from append()ed entries, so the backend never sees it.
        (form as any).hideLoader = true;

        return this.http.post<any>(`${this.baseUrl()}${options.api ?? this.uploadApi}`, form).pipe(
            catchError(() => of(null))
        );
    }

    private baseUrl(): string {
        const stored = localStorage.getItem('baseUrl');
        return (!stored || stored === 'undefined') ? environment.baseUrl : stored;
    }

    /**
     * capture -> upload to s3 -> resolve with the s3 url.
     */
    async captureAndUpload(options: ScreenshotOptions = {}, throwOnError = false): Promise<ScreenshotUploadResponse> {
        const alreadyUploaded = this.getCachedUrl(options.cacheKey);
        if (alreadyUploaded) {
            return { success: true, url: alreadyUploaded, cached: true };
        }

        try {
            const blob = await this.capture(options);
            const response = await firstValueFrom(this.upload(blob, options));
            const result = this.toResult(response, options.cacheKey);

            if (!result.success && throwOnError) {
                throw new Error(result.error);
            }

            return result;
        } catch (error: any) {
            if (throwOnError) {
                throw error;
            }
            return { success: false, error: error?.message ?? 'Screenshot upload failed' };
        }
    }

    /**
     * observable made, so it can be chained straight into an existing pipe
     */
    captureAndUpload$(options: ScreenshotOptions = {}): Observable<ScreenshotUploadResponse> {
        return defer(() => {
            const alreadyUploaded = this.getCachedUrl(options.cacheKey);
            if (alreadyUploaded) {
                return of({ success: true, url: alreadyUploaded, cached: true });
            }
            return from(this.capture(options)).pipe(
                switchMap((blob) => this.upload(blob, options)),
                map((response) => this.toResult(response, options.cacheKey))
            );
        }).pipe(
            catchError((error: any) => of({
                success: false,
                error: error?.message ?? 'Screenshot upload failed'
            }))
        );
    }

    /** s3 url of an earlier upload for this key, '' when nothing is stored. */
    getCachedUrl(cacheKey?: string): string {
        if (!cacheKey) {
            return '';
        }
        return this.readCache()[cacheKey] ?? '';
    }

    /**
     * Drops a remembered url - call it once the payment goes through, so the
     * next payment starts with a fresh screenshot.
     */
    clearCachedUrl(cacheKey?: string): void {
        if (!cacheKey) {
            localStorage.removeItem(this.cacheStorageKey);
            return;
        }
        const cache = this.readCache();
        delete cache[cacheKey];
        this.writeCache(cache);
    }

    private toResult(response: any, cacheKey?: string): ScreenshotUploadResponse {
        if (!response || response.is_error === 1 || response.is_error === true) {
            return {
                success: false,
                error: response?.override_text ?? response?.err ?? response?.error ?? 'Screenshot upload failed',
                response
            };
        }

        const url = this.extractUrl(response);

        if (cacheKey && url) {
            const cache = this.readCache();
            cache[cacheKey] = url;
            this.writeCache(cache);
        }

        return { success: true, url, response, cached: false };
    }

    private readCache(): Record<string, string> {
        try {
            return JSON.parse(localStorage.getItem(this.cacheStorageKey) ?? '{}');
        } catch {
            return {};
        }
    }

    private writeCache(cache: Record<string, string>): void {
        try {
            localStorage.setItem(this.cacheStorageKey, JSON.stringify(cache));
        } catch {
            // storage full / private mode - caching is best effort only
        }
    }

    private extractUrl(response: any): string {
        return response?.url ?? '';
    }

    private buildFileName(mimeType: string): string {
        const extension = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg';
        return `screenshot_${Date.now()}.${extension}`;
    }
}
